import { Component, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { CoordinatePointModel, VertexCSVContent, VertexCsvModel } from 'src/app/models/coordinate-point';
import { ConfigurationConstants, CoordinatePointTypes } from 'src/app/models/EnumTypes';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { UtilsService } from 'src/app/services/utils.service';
import { CoordpointidStatusComponent } from '../coordpointid-status/coordpointid-status.component';
import { ImportFormat } from 'src/app/models/importFormat';

@Component({
  selector: 'app-airspace-vertex',
  templateUrl: './airspace-vertex.component.html',
  styleUrls: ['../../../app.component.scss'],
  standalone: false
})
export class AirspaceVertexComponent implements OnInit {

  public vertexImportCsvModel: VertexCsvModel;

  importFormat: ImportFormat = ImportFormat.CSV_FORMAT;

  constructor(
    private modalService: ModalControlService,
    public bsModalRef: BsModalRef,
    private ngxCsvParser: NgxCsvParser,
    private utilsService: UtilsService,
    public popupService: BsModalService
  ) {
    this.vertexImportCsvModel = new VertexCsvModel();
  }

  ngOnInit(): void {
    this.vertexImportCsvModel.fillTable();
  }

  cancelAirspaceVertex(): void {
    this.bsModalRef.hide();
  }

  saveFile(event: any): void {
    this.vertexImportCsvModel.file = event.target.files[0];
  }

  startImport(): void {

    if (this.vertexImportCsvModel.file == null) {
      this.modalService.showMesssageModal("Error", "You have to choose a file before importing it.");
      return;
    }

    if (!this.ngxCsvParser.isCSVFile(this.vertexImportCsvModel.file)) {
      this.modalService.showMesssageModal("Error", 'The inserted file has an invalid extension. Insert a "csv" file.');
      return;
    }

    if (this.vertexImportCsvModel.decimalSeparator == this.vertexImportCsvModel.fieldSeparator) {
      this.modalService.showMesssageModal("Error", 'Please define a different field and decimal separator.');
      return;
    }

    this.ngxCsvParser
      .parse(this.vertexImportCsvModel.file, { header: false, delimiter: this.vertexImportCsvModel.fieldSeparator })
      .subscribe((result: Array<any>) => {
        let previousLineLength: number = result[this.vertexImportCsvModel.dataStartingLine - 1].length;
        for (let i: number = this.vertexImportCsvModel.dataStartingLine - 1; i < result.length; i++) {
          if (result[i][result[i].length - 1] === "") { // in cases where the lines in the file end with the separator
            result[i].pop();
          }
          if (result[i].length == previousLineLength) {
            // The system was able to parse successfully the CSV file.
            const vertexToImport: VertexCSVContent = VertexCSVContent.map(result[i], this.vertexImportCsvModel.table);
            this.vertexImportCsvModel.fileContent.push(vertexToImport);
          } else {
            // The system was not able to parse successfully the CSV file.
            this.modalService.showMesssageModal("Import Vertex", "Error encountered in line " + (i + 1) + ".");
            return;
          }
          previousLineLength = result[i].length;
        }

        const errors: string[] = this.validateContent(this.vertexImportCsvModel.fileContent);

        if (errors.length != 0) {
          this.modalService.showMultipleMesssagesModal("Error Importing", errors);
          return;
        }
        const published: boolean = this.vertexImportCsvModel.fileDataSource == "Published";
        const coordPoints: CoordinatePointModel[] = this.parseImportedVertexInfoIntoCoordPoints(this.vertexImportCsvModel.fileContent, published);
        const bsRef: BsModalRef = this.popupService.show(CoordpointidStatusComponent);
        bsRef.content.coordPoints = CoordpointidStatusComponent.getListWithDuplicates(coordPoints);
        bsRef.content.modalId = bsRef.id;

      }, (error: NgxCSVParserError) => {
        this.modalService.showMesssageModal("Error", "Unable to extract information in CSV.");
      },
        () => {
          this.bsModalRef.hide();
        });
  }

  /**
   * Parses vertex imported information rows into coordinate points
   * 
   * @param importedVertexInfo
   *            Imported information rows
   * @param isIdPresent
   *            Is the id set to be imported
   * @param published
   *            Is the file source a published source
   * @return List of coordinate points
   */
  private parseImportedVertexInfoIntoCoordPoints(importedVertexInfo: VertexCSVContent[], published: boolean): CoordinatePointModel[] {
    const toRet: CoordinatePointModel[] = [];
    for (const info of importedVertexInfo) {
      const point: CoordinatePointModel = new CoordinatePointModel();
      point.type = "AIRSPACE_BOUNDARY_VERTEX";
      point.userdefined = !published;
      point.coordpointid = info.coordpointId;
      point.latitude = Number.parseFloat(info.latitude);
      point.longitude = Number.parseFloat(info.longitude);
      point.maxaltitude = this.parseDistance(Number.parseFloat(info.upperVerticalDistanceValue), info.upperVerticalDistanceUnits);
      point.minaltitude = this.parseDistance(Number.parseFloat(info.lowerVerticalDistanceValue), info.lowerVerticalDistanceUnits);
      point.name = info.airspaceName;

      toRet.push(point);
    }
    return toRet;
  }

  /**
   * Parse distance
   */
  private parseDistance(value: number, unit: string): number {
    if (value == null || unit == null)
      return null;
    switch (unit) {
      case "FL":
        return this.utilsService.flToFeet(value);
      case "FT":
        return value;
      case "M":
        return this.utilsService.metersToFeet(value);
      default:
        return null;
    }
  }

  validateContent(fileContent: VertexCSVContent[]): string[] {
    let errors: string[] = [];
    let currentIdentifier: string;
    let errorMessage: string;
    const usedIds: string[] = [];
    fileContent.forEach((line, index) => {
      if (!this.stringNullOrEmpty(line.identifier)) {
        currentIdentifier = line.identifier;
      } else {
        if (!this.stringNullOrEmpty(line.codeId)) {
          let i: number = 0;
          while (usedIds.includes(line.codeId + i)) {
            i++;
          }
          let codeId: string;
          if (line.codeId.length > 3) {
            codeId = line.codeId.substring(0, 3);
          } else {
            codeId = line.codeId;
          }
          currentIdentifier = codeId + i;
          usedIds.push(line.codeId + i);
        } else {
          currentIdentifier = "Vertex";
        }
      }

      line.coordpointId = currentIdentifier;

      if (this.stringNullOrEmpty(line.codeId)) {
        errorMessage = "Property Code ID cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }
      if (this.stringNullOrEmpty(line.airspaceName)) {
        errorMessage = "Property Airspace Name cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }
      if (this.stringNullOrEmpty(line.latitude)) {
        errorMessage = "Property Latitude cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidLat(line.latitude)) {
        errorMessage = "Property Latitude is not a valid number";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }
      if (this.stringNullOrEmpty(line.longitude)) {
        errorMessage = "Property Longitude cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidLon(line.longitude)) {
        errorMessage = "Property Longitude is not a valid number";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }
    });
    return errors;
  }

  private buildErrorMessage(identifier: string, errorMessage: string, index: number): string {
    return `Vertex: ${identifier} - ${errorMessage}. Line ${(index + this.vertexImportCsvModel.dataStartingLine)}.\n`;
  }

  private stringNullOrEmpty(string: string): boolean {
    if (string) {
      return false;
    } else {
      return true;
    }
  }

  private isValidFloat(floatString: string): boolean {
    let floatNumber: Number = Number(floatString);
    if (Number.isNaN(floatNumber)) {
      return false;
    }
    return true;
  }

  private isValidLat(lat: string): boolean {
    try {
      let latDouble: number = Number(lat);
      if (Number.isNaN(latDouble)) {
        return false;
      }
      latDouble = Number.parseFloat(lat);
      if (latDouble <= ConfigurationConstants.LATITUDE_MAX && latDouble >= ConfigurationConstants.LATITUDE_MIN) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }

  private isValidLon(lon: string): boolean {
    try {
      let lonDouble: number = Number(lon);
      if (Number.isNaN(lonDouble)) {
        return false;
      }
      lonDouble = Number.parseFloat(lon);
      if (lonDouble <= ConfigurationConstants.LONGITUDE_MAX && lonDouble >= ConfigurationConstants.LONGITUDE_MIN) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }

}
